import React, { useCallback, useContext } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyLuxPlusTieredRenewalRewardEnabled from 'loyalty/selectors/getIsLoyaltyLuxPlusTieredRenewalRewardEnabled'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import LuxPlusRenewalBonusModal from '../modals/LuxPlusRenewalBonusModal'

// Mirrors the interactive tile styling in LuxPlusLandingBenefitTileItem; kept local because this
// tile opens a modal rather than being driven by the benefit-view data/CTA system.
const BenefitTile = styled.button`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  padding: ${rem(20)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  cursor: pointer;
  text-align: start;
  width: 100%;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)};
  }
`

const BenefitContent = styled.div`
  flex: 1;
  min-width: 0;
`

const messages = defineMessages({
  title: {
    id: '0rzFbApGSlBY5EDSHX7T',
    defaultMessage: 'More years, more rewards',
    description: 'Title of the renewal bonus benefit tile on the LuxPlus landing page',
  },
  description: {
    id: 'mp62dDNHTr2rv9FVa9Gp',
    defaultMessage: 'Earn bonus Société Points and Status Credits each time you renew your membership.',
    description: 'Description of the renewal bonus benefit tile on the LuxPlus landing page',
  },
  // Infinite members earn no Status Credits, so the tile promises points only.
  descriptionInfinite: {
    id: 'tbZRE-mmLdOCaEPoCU1p',
    defaultMessage: 'Earn bonus Société Points each time you renew your membership.',
    description: 'Description of the renewal bonus benefit tile for an Infinite member (points only, no Status Credits)',
  },
})

function LuxPlusRenewalBonusBenefitTile() {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const isEnabled = useAppSelector(getIsLoyaltyLuxPlusTieredRenewalRewardEnabled)
  const isInfinite = useAppSelector(getLoyaltyIsInfiniteAccountTier)

  const handleClick = useCallback(() => {
    showModal(<LuxPlusRenewalBonusModal />)
  }, [showModal])

  if (!isEnabled) return null

  return (
    <BenefitTile type="button" onClick={handleClick}>
      <LineGiftIcon colour="lux-plus" size="L" />
      <BenefitContent>
        <BodyText variant="medium" weight="bold">{intl.formatMessage(messages.title)}</BodyText>
        <BodyText variant="medium" colour="light-foreground-alt">{intl.formatMessage(isInfinite ? messages.descriptionInfinite : messages.description)}</BodyText>
      </BenefitContent>
      <LineAngleRightIcon size="S" colour="light-foreground-alt" />
    </BenefitTile>
  )
}

export default LuxPlusRenewalBonusBenefitTile
